import { ReactElement, useEffect, useState } from 'react';
import { initialAllEndUsersFilter } from '.';
import { UserCard } from '../userCard/userCard';
import { useFilterStore } from '@store/index';
import { EndUserFilterStore } from '@store/memberList';
import { useInfiniteScroll } from '@components/common';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useLmsUserToggle } from '@hooks/useLmsUserToggle';
import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';
import { ToastType, useTosterStore } from '@store/toster';
import { DataRow } from '@helper/utils';
import { ExportXlsxFile } from '@helper/exportXlsxFile';
import { GetRoleBasedMemberListData } from '@services/hooks/endusers/useRoleBasedMemberListData';
import { useAllEndUserQuery } from '@services/hooks/endusers/allEndUserQuery';
interface RBDMEndUserExportData {
	id: number;
	sfid: string;
	phone: string;
	mdicode: string;
	is_a_ob: boolean;
	name: string;
	status: string;
	createddate: string;
	clubname: string;
	contractorclass: string;
	competitionconsumption: number;
	glue_consumption_per_month: number;
	priority: number | null;
	totalbankingQTD: number | null;
	visitstatus: string;
	lastactivitydate: string;
	pwg_banking_qtd: number | null;
	membershipno: string;
	currenttier: string | null;
	banking_six_month_kgs: number | null;
	total_points_per_kgs: number | null;
	banking_points_qtd: number | null;
	kyc_id_proof_verification: string;
	contractorconsumption: string;
	club_mapping: string;
	is_zbc: boolean;
	is_de_growing: boolean;
	clustercode_c: string;
	clustername_c: string | null;
	lastmodifieddate: string;
	personcontactid: string;
	is_adopted_by: boolean;
	basic_details_updated: string;
	more_details_updated: string;
	parent_member_id_c: string | null;
	banking_mtd_kgs: number | null;
	fpc_supervisor_c: boolean;
	source_c: string;
	created_by: string;
}

const formatDateToDDMMYYYY = (dateString: string | null): string => {
	if (!dateString) return '';

	try {
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '';

		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear().toString();

		return `${day}-${month}-${year}`;
	} catch (error) {
		return '';
	}
};

export const AllEndUserList = (): ReactElement => {
	const { isFetching: queryFetching, error } = useAllEndUserQuery();
	const { updateSearch, updateSortObject, updateFilterObject, updateSortValue, updateSortActiveStatus, filterObject } =
		useFilterStore((state) => state);
	const { allEndUserFilter, updateAllEndUserFilter } = EndUserFilterStore((state) => state);
	const { fetchMore, isFetching, result } = GetRoleBasedMemberListData();
	const checkSubContractorPermission = useComponentLevelAccessCheck('sub-contractor');
	const userRole = useUserRoleType();
	const { setToaster } = useTosterStore();
	const [isExporting, setIsExporting] = useState(false);

	const handleExportRBDMEndUsers = async (): Promise<void> => {
		try {
			setIsExporting(true);

			// Fetch all RBDM end user data from API
			const response = await APIService.getData<RBDMEndUserExportData[]>(APICONFIG.ALL_END_USER_API_RBDM, {
				limit: '0',
			});

			if (response && response.length > 0) {
				// Transform the API response to Excel format
				const excelData: DataRow[] = response.map((user) => ({
					ID: user.id,
					SFID: user.sfid || '',
					Phone: user.phone || '',
					'MDI Code': user.mdicode || '',
					'Is OB': user.is_a_ob ? 'Yes' : 'No',
					Name: user.name || '',
					Status: user.status || '',
					'Created Date': formatDateToDDMMYYYY(user.createddate) || '',
					'Club Name': user.clubname || '',
					'Contractor Class': user.contractorclass || '',
					'Competition Consumption': user.competitionconsumption || 0,
					'Glue Consumption Per Month': user.glue_consumption_per_month || 0,
					Priority: user.priority ?? '',
					'Total Banking QTD': user.totalbankingQTD ?? '',
					'Visit Status': user.visitstatus || '',
					'Last Activity Date': user.lastactivitydate || '',
					'PWG Banking QTD': user.pwg_banking_qtd ?? '',
					'Membership No': user.membershipno || '',
					'Current Tier': user.currenttier ?? '',
					'Banking Six Month KGS': user.banking_six_month_kgs ?? '',
					'Total Points Per KGS': user.total_points_per_kgs ?? '',
					'Banking Points QTD': user.banking_points_qtd ?? '',
					'KYC ID Proof Verification': user.kyc_id_proof_verification || '',
					'Contractor Consumption': user.contractorconsumption || '',
					'Club Mapping': user.club_mapping || '',
					'Is ZBC': user.is_zbc ? 'Yes' : 'No',
					'Is De Growing': user.is_de_growing ? 'Yes' : 'No',
					'Cluster Code': user.clustercode_c || '',
					'Cluster Name': user.clustername_c ?? '',
					'Last Modified Date': formatDateToDDMMYYYY(user.lastmodifieddate) || '',
					'Person Contact ID': user.personcontactid || '',
					'Is Adopted By': user.is_adopted_by ? 'Yes' : 'No',
					'Basic Details Updated': formatDateToDDMMYYYY(user.basic_details_updated) || '',
					'More Details Updated': formatDateToDDMMYYYY(user.more_details_updated) || '',
					'Parent Member ID': user.parent_member_id_c ?? '',
					'Banking MTD KGS': user.banking_mtd_kgs ?? '',
					'FPC Supervisor': user.fpc_supervisor_c ? 'Yes' : 'No',
					Source: user.source_c || '',
					'Created By': user.created_by || '',
				}));

				// Export to Excel using existing utility
				ExportXlsxFile(excelData, 'RBDM_End_Users');
				setToaster(true, ToastType.success, 'RBDM End Users exported successfully!');
			} else {
				setToaster(true, ToastType.error, 'No RBDM end user data available for export');
			}
		} catch (error) {
			setToaster(true, ToastType.error, 'Failed to export RBDM end users data');
		} finally {
			setIsExporting(false);
		}
	};

	useEffect(() => {
		updateSortObject([
			{ value: 'priority', name: 'Priority Order' },
			{ value: 'glue_consumption_per_month', name: 'Glue Potential' },
			{ value: 'pwg_banking_qtd', name: 'PWG Banking in Quarter' },
		]);
		updateSortValue('');
		updateSortActiveStatus(false);
		updateSearch('');
		if (allEndUserFilter === null) {
			updateFilterObject({
				...initialAllEndUsersFilter,
				customToggle: {
					flagged: true,
					data: [
						{ value: 'OB' },
						{ value: 'ZBC' },
						{ value: 'Degrowing' },
						{ value: 'Adopted' },
						{ value: 'Disengaged' },
						{ value: 'SSM' },
						...(!checkSubContractorPermission ? [{ value: 'Sub Contractor' }] : []),
					],
					toggleData: [],
				},
				largeContractor: {
					flagged: true,
					name: 'Large Contractor',
					dropDownData: '',
					value: '',
				},
				mdicodeDropdown: {
					flagged: userRole === 'RBDM',
					name: 'MDI Code',
					data: [],
					dropDownData: '',
				},
				rigidGluePotential: { flagged: true, enabled: false },
			});
			return;
		}

		updateFilterObject({
			...allEndUserFilter,
			customToggle: {
				...allEndUserFilter.customToggle,
				data: allEndUserFilter.customToggle.data.filter((item) => item.value !== 'Rigid Contractor glue potential'),
				toggleData: allEndUserFilter.customToggle.toggleData.filter(
					(item) => item !== 'Rigid Contractor glue potential'
				),
			},
			rigidGluePotential: {
				flagged: true,
				enabled: allEndUserFilter.rigidGluePotential?.enabled ?? false,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		updateAllEndUserFilter(filterObject);
	}, [filterObject, updateAllEndUserFilter]);

	useLmsUserToggle();

	useInfiniteScroll(() => {
		fetchMore();
	});

	return (
		<Wrapper isLoaderVisible={queryFetching || (isFetching && result.length === 0)} isError={error}>
			<>
				{userRole === 'RBDM' && (
					<div className="export-btn-wrapper">
						<button onClick={handleExportRBDMEndUsers} disabled={isExporting} className="btn btn-primary">
							{isExporting ? 'Exporting...' : 'Export RBDM End Users'}
						</button>
					</div>
				)}
				<div className="user-card-list-wrapper">
					<ul className="user-card-list row">
						{result.length > 0 ? (
							result.map((endUser, index) => {
								return <UserCard key={endUser?._data.id} userObject={endUser._data} index={index} state={'/all'} />;
							})
						) : (
							<li className="d-col d-col-2">
								<p className="h3">No Data Found!</p>
							</li>
						)}
					</ul>
				</div>
			</>
		</Wrapper>
	);
};
